import { memo, useState, useCallback, useRef, type MouseEvent, type RefObject } from 'react';
import type { Project } from '@/api/client';
import { formatDecimal } from '@/utils/formatNumber';
import './ProgressBarWithTooltip.css';

function getTooltipData(project: Project, plannedSessionsCount?: number) {
  const sessions = project.sessions ?? [];
  const readers = project.readers ?? [];
  const planned =
    plannedSessionsCount !== undefined
      ? plannedSessionsCount
      : sessions.filter((s) => !s.is_recorded && !s.is_canceled).length;
  const singleReader = readers.length === 1 ? readers[0] : null;
  const remaining = singleReader?.remaining_sessions;
  const remainingStr = remaining != null && remaining !== '' ? formatDecimal(remaining) : 'N/A';
  const avgPerHour =
    readers.length > 0
      ? readers.reduce((sum, r) => {
          const raw = r.avg_pages_per_hour;
          const n = typeof raw === 'string' ? parseFloat(raw) : raw;
          return sum + (n != null && !isNaN(n) ? n : 0);
        }, 0) / readers.length
      : 0;
  const avgPerHourStr = avgPerHour > 0 ? formatDecimal(avgPerHour) : '—';
  return { planned, remainingStr, avgPerHourStr };
}

interface ProgressBarWithTooltipProps {
  project: Project;
  progress: number;
  /** Запланировано сессий из сводки, если в проект не подгружен полный список */
  plannedSessionsCount?: number;
  detailsAnchorRef?: RefObject<HTMLElement | null>;
  onOpenProjectDetails?: (anchorEl: HTMLElement | null) => void;
}

export const ProgressBarWithTooltip = memo(function ProgressBarWithTooltip({
  project,
  progress,
  plannedSessionsCount,
  detailsAnchorRef,
  onOpenProjectDetails,
}: ProgressBarWithTooltipProps) {
  const [hovered, setHovered] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const onMouseEnter = useCallback(() => setHovered(true), []);
  const onMouseLeave = useCallback(() => setHovered(false), []);

  const { planned, remainingStr, avgPerHourStr } = getTooltipData(project, plannedSessionsCount);
  const readers = project.readers ?? [];
  const multiReader = readers.length > 1;

  const handleOpenDetailsClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const anchor = detailsAnchorRef?.current ?? rootRef.current;
      onOpenProjectDetails?.(anchor);
    },
    [onOpenProjectDetails, detailsAnchorRef],
  );

  const pct = Math.min(100, Math.max(0, Math.round(Number(progress) || 0)));

  return (
    <div
      ref={rootRef}
      className="projects-table__name-progress-wrap"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {hovered && (
        <div
          className={
            multiReader
              ? 'projects-table__progress-tooltip projects-table__progress-tooltip_multi-reader'
              : 'projects-table__progress-tooltip'
          }
        >
          {multiReader ? (
            <>
              <button
                type="button"
                className="projects-table__progress-tooltip-open-details"
                onClick={handleOpenDetailsClick}
              >
                Открыть детализацию
              </button>
              <div className="projects-table__progress-tooltip-row">
                <span>Запланировано:</span>
                <span>{planned}</span>
              </div>
            </>
          ) : (
            <>
              <div className="projects-table__progress-tooltip-row">
                <span>В среднем:</span>
                <span>
                  {avgPerHourStr}
                  {avgPerHourStr !== '—' ? ' стр/ч' : ''}
                </span>
              </div>
              <div className="projects-table__progress-tooltip-row">
                <span>Осталось:</span>
                <span>{remainingStr}</span>
              </div>
              <div className="projects-table__progress-tooltip-row">
                <span>Запланировано:</span>
                <span>{planned}</span>
              </div>
            </>
          )}
        </div>
      )}
      <div className="projects-table__name-progress-row">
        <div
          className="projects-table__name-progress-track"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="projects-table__progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="projects-table__name-progress-pct">{pct}%</span>
      </div>
    </div>
  );
});
